import {
  dismissBrowserNotificationsWhenOpeningChat,
  getActiveConversationIdFromLocation,
} from '@common/utils/push-notification-chat';

/** Message type posted to the OneSignal service worker. */
export const ACTIVE_CHAT_CONVERSATION_SW_MESSAGE = 'ACTIVE_CHAT_CONVERSATION';

const ACTIVE_CHAT_STORAGE_KEY = 'oneteam_active_chat_conversation_id';
const ACTIVE_CHAT_STORAGE_MAX_AGE_MS = 30 * 60 * 1000;

let activeConversationId: string | null = null;

function readActiveConversationFromStorage(): string | null {
  if (typeof localStorage === 'undefined') return null;

  try {
    const raw = localStorage.getItem(ACTIVE_CHAT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { id?: string; at?: number };
    if (!parsed.id || typeof parsed.at !== 'number') return null;
    if (Date.now() - parsed.at > ACTIVE_CHAT_STORAGE_MAX_AGE_MS) {
      localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
      return null;
    }
    return parsed.id;
  } catch {
    return null;
  }
}

function writeActiveConversationToStorage(conversationId: string | null): void {
  if (typeof localStorage === 'undefined') return;

  try {
    if (!conversationId) {
      localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
      return;
    }

    localStorage.setItem(
      ACTIVE_CHAT_STORAGE_KEY,
      JSON.stringify({ id: conversationId, at: Date.now() })
    );
  } catch {
    // Blocked storage should not break in-memory active chat tracking.
  }
}

function syncActiveConversationToServiceWorker(conversationId: string | null): void {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;

  const message = {
    type: ACTIVE_CHAT_CONVERSATION_SW_MESSAGE,
    conversationId,
  };

  const post = (worker: ServiceWorker | null | undefined) => {
    if (worker) worker.postMessage(message);
  };

  post(navigator.serviceWorker.controller);

  void navigator.serviceWorker.ready.then((registration) => {
    post(registration.active);
    registration.waiting?.postMessage(message);
    registration.installing?.postMessage(message);
  }).catch(() => {
    // Jest / SSR — no service worker registration
  });
}

/** Keep tracker aligned with the URL (SPA navigation, deep links, refresh). */
export function syncActiveConversationFromRoute(
  pathname: string,
  search: string = ''
): void {
  const fromRoute = getActiveConversationIdFromLocation(pathname, search) ?? null;
  const previousRouteConversationId = activeConversationId;
  activeConversationId = fromRoute;
  writeActiveConversationToStorage(fromRoute);
  syncActiveConversationToServiceWorker(fromRoute);

  if (fromRoute && fromRoute !== previousRouteConversationId) {
    dismissBrowserNotificationsWhenOpeningChat(fromRoute);
  }
}

export function setActiveConversationIdForPush(
  conversationId: string | null | undefined
): void {
  activeConversationId = conversationId || null;
  writeActiveConversationToStorage(activeConversationId);
  syncActiveConversationToServiceWorker(activeConversationId);
}

export function getActiveConversationIdForPush(): string | null {
  return activeConversationId || readActiveConversationFromStorage();
}

/** Sync in-memory id when another tab updates localStorage. */
export function bindActiveConversationStorageSync(): () => void {
  if (typeof window === 'undefined') return () => {};

  const onStorage = (event: StorageEvent) => {
    if (event.key !== ACTIVE_CHAT_STORAGE_KEY) return;
    activeConversationId = readActiveConversationFromStorage();
    syncActiveConversationToServiceWorker(activeConversationId);
  };

  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}
